import React, { useEffect, useRef, useState } from 'react';
import {
  IconChevronDown,
  IconCopy,
  IconStar,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSettings,
  IconTrash,
} from '../Icons';
import { MAX_DASHBOARDS } from './dashboardApi';
import { VECTOR_DASHBOARD_ID, orgDashboardName } from './vectorDashboardTemplate';

/** The brand's org-wide board is always named after the active brand. */
function dashboardLabel(d) {
  if (!d) return '';
  return d.is_system || d.id === VECTOR_DASHBOARD_ID ? orgDashboardName() : d.name;
}

const IconMore = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
  </svg>
);

/**
 * Dashboard tabs + manage menu + customize CTA — shared by Overview and Overview Test.
 * Every dashboard is one click away as a tab; less common actions live in the ⋯ menu.
 */
export default function DashboardToolbar({
  dashboards,
  current,
  customizing,
  canCustomize,
  canEditVector,
  onSwitchDashboard,
  onCreate,
  onDuplicate,
  onRename,
  onDelete,
  onMakeDefault,
  onResetLayout,
  onToggleCustomize,
  onCreateOwnDashboard,
  onUpgrade,
  saving,
  saveError,
  /** When true, show Reset layout on system dashboards (Overview Test admin sandbox). */
  showSystemResetLayout = false,
}) {
  const isCurrentSystem = !!current?.is_system;
  const personalCount = dashboards.filter((d) => !d.is_system).length;
  const atLimit = personalCount >= MAX_DASHBOARDS;
  const limitTitle = `Limit reached (${MAX_DASHBOARDS} dashboards). Delete one to add another.`;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(current?.name || '');
  const [editingName, setEditingName] = useState(false);
  const pickerRef = useRef(null);
  const tabsRef = useRef(null);
  const ordered = [...dashboards.filter((d) => d.is_system), ...dashboards.filter((d) => !d.is_system)];

  // Keep the active tab in view when switching (long lists scroll sideways).
  useEffect(() => {
    const el = tabsRef.current?.querySelector('.dashboard-tab.active');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [current?.id]);

  useEffect(() => {
    setRenameValue(current?.name || '');
    setEditingName(false);
  }, [current?.id, current?.name]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const submitRename = () => {
    const trimmed = (renameValue || '').trim();
    if (trimmed && trimmed !== current?.name) onRename(trimmed);
    setEditingName(false);
  };

  const showNameEditor = editingName && !isCurrentSystem && canCustomize;

  return (
    <div className="dashboard-toolbar">
      <div className="dashboard-toolbar-left">
        <div className="dashboard-tabs" ref={tabsRef} role="tablist" aria-label="Dashboards">
          {ordered.map((d) => {
            const active = d.id === current?.id;
            if (active && showNameEditor) {
              return (
                <input
                  key={d.id}
                  autoFocus
                  type="text"
                  className="dashboard-name-input dashboard-tab-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={submitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitRename();
                    if (e.key === 'Escape') {
                      setRenameValue(current?.name || '');
                      setEditingName(false);
                    }
                  }}
                  maxLength={80}
                  aria-label="Dashboard name"
                />
              );
            }
            return (
              <button
                key={d.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`dashboard-tab${active ? ' active' : ''}${d.is_system ? ' is-company' : ''}`}
                onClick={() => { if (!active) onSwitchDashboard(d.id); }}
                onDoubleClick={() => { if (active && !d.is_system && canCustomize) setEditingName(true); }}
                title={active && !d.is_system && canCustomize ? 'Double-click to rename' : undefined}
              >
                {d.is_default ? <span className="dashboard-tab-default" title="Default dashboard"><IconStar size={11} /></span> : null}
                <span className="dashboard-tab-label">{dashboardLabel(d)}</span>
              </button>
            );
          })}
        </div>
        <div className="dashboard-tab-actions" ref={pickerRef}>
          {/* Phones: one compact dropdown instead of tabs + buttons. */}
          <button
            type="button"
            className={`dashboard-mobile-picker${pickerOpen ? ' is-open' : ''}`}
            onClick={() => setPickerOpen((v) => !v)}
            aria-expanded={pickerOpen}
            aria-label="Choose dashboard"
          >
            {current?.is_default ? <span className="dashboard-tab-default"><IconStar size={11} /></span> : null}
            <span className="dashboard-tab-label">{dashboardLabel(current) || 'Dashboard'}</span>
            <span className="dashboard-mobile-picker-chev"><IconChevronDown size={16} /></span>
          </button>
          {canCustomize ? (
            <button
              type="button"
              className="dashboard-tab-icon"
              onClick={onCreate}
              disabled={atLimit}
              aria-label="New dashboard"
              data-tip={atLimit ? `Limit: ${MAX_DASHBOARDS} dashboards` : 'New dashboard'}
            >
              <IconPlus size={15} />
            </button>
          ) : null}
          <button
            type="button"
            className={`dashboard-tab-icon${pickerOpen ? ' is-open' : ''}`}
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="Manage dashboards"
            aria-expanded={pickerOpen}
            data-tip="Manage dashboards"
          >
            <IconMore size={16} />
          </button>
          {pickerOpen ? (
            <div className="dashboard-name-menu" role="menu">
              {/* Dashboard list: phones only (desktop shows tabs). */}
              <div className="dashboard-name-menu-section dashboard-menu-switch">
                {ordered.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={`dashboard-name-menu-item ${d.id === current?.id ? 'active' : ''}`}
                    onClick={() => { setPickerOpen(false); if (d.id !== current?.id) onSwitchDashboard(d.id); }}
                    role="menuitem"
                  >
                    <span>{dashboardLabel(d)}</span>
                    {d.is_default ? <span className="dashboard-tab-default"><IconStar size={11} /></span> : null}
                  </button>
                ))}
              </div>
              {!canCustomize ? (
                <div className="dashboard-name-menu-locked-region">
                  <div className="dashboard-name-menu-section">
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={`example-${n}`}
                        type="button"
                        className="dashboard-name-menu-item dashboard-name-menu-item-example"
                        disabled
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        <span>{`Dashboard ${n}`}</span>
                      </button>
                    ))}
                  </div>
                  <div className="dashboard-name-menu-section dashboard-name-menu-actions">
                    <button
                      type="button"
                      className="dashboard-name-menu-item"
                      disabled
                      aria-hidden="true"
                      tabIndex={-1}
                    >
                      <IconPlus size={14} /> New dashboard
                    </button>
                    <button
                      type="button"
                      className="dashboard-name-menu-item"
                      disabled
                      aria-hidden="true"
                      tabIndex={-1}
                    >
                      <IconCopy size={14} /> Duplicate
                    </button>
                  </div>
                  <div className="dashboard-name-menu-upgrade-overlay">
                    <div className="dashboard-name-menu-upgrade-card">
                      <p className="dashboard-name-menu-upgrade-message">
                        Upgrade to RiskLo Pro to create up to 5 custom dashboards.
                      </p>
                      <button
                        type="button"
                        className="dashboard-toolbar-btn dashboard-toolbar-btn-upgrade"
                        onClick={() => {
                          setPickerOpen(false);
                          if (typeof onUpgrade === 'function') {
                            onUpgrade('Upgrade to RiskLo Pro to create and customize your own dashboards.');
                          }
                        }}
                      >
                        Upgrade to RiskLo Pro
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="dashboard-name-menu-section dashboard-name-menu-actions">
                  <button
                    type="button"
                    className="dashboard-name-menu-item"
                    onClick={() => {
                      setPickerOpen(false);
                      onCreate();
                    }}
                    disabled={atLimit}
                    title={atLimit ? limitTitle : undefined}
                  >
                    <IconPlus size={14} /> New dashboard
                  </button>
                  <button
                    type="button"
                    className="dashboard-name-menu-item"
                    onClick={() => {
                      setPickerOpen(false);
                      onDuplicate();
                    }}
                    disabled={!current || atLimit}
                    title={atLimit ? limitTitle : undefined}
                  >
                    <IconCopy size={14} /> Duplicate
                  </button>
                  {current && !current.is_default ? (
                    <button
                      type="button"
                      className="dashboard-name-menu-item"
                      onClick={() => {
                        setPickerOpen(false);
                        onMakeDefault();
                      }}
                    >
                      Set as default
                    </button>
                  ) : null}
                  {!isCurrentSystem ? (
                    <>
                      <button
                        type="button"
                        className="dashboard-name-menu-item"
                        onClick={() => {
                          setPickerOpen(false);
                          setEditingName(true);
                        }}
                        disabled={!current}
                      >
                        <IconPencil size={14} /> Rename
                      </button>
                      <button
                        type="button"
                        className="dashboard-name-menu-item"
                        onClick={() => {
                          setPickerOpen(false);
                          onResetLayout();
                        }}
                        disabled={!current}
                      >
                        <IconRefresh size={14} /> Reset layout
                      </button>
                      <button
                        type="button"
                        className="dashboard-name-menu-item dashboard-name-menu-item-danger"
                        onClick={() => {
                          setPickerOpen(false);
                          onDelete();
                        }}
                        disabled={!current}
                      >
                        <IconTrash size={14} /> Delete dashboard
                      </button>
                    </>
                  ) : null}
                  {isCurrentSystem && showSystemResetLayout ? (
                    <button
                      type="button"
                      className="dashboard-name-menu-item"
                      onClick={() => {
                        setPickerOpen(false);
                        onResetLayout();
                      }}
                    >
                      <IconRefresh size={14} /> Reset layout
                    </button>
                  ) : null}
                </div>
              )}
              {canCustomize ? (
                <div className={`dashboard-name-menu-footer ${atLimit ? 'is-at-limit' : ''}`}>
                  {atLimit
                    ? `You've reached the ${MAX_DASHBOARDS}-dashboard limit. Delete one to add another.`
                    : `${personalCount} of ${MAX_DASHBOARDS} dashboards used`}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {saving ? <span className="dashboard-toolbar-saving" aria-live="polite">Saving…</span> : null}
        {!saving && saveError ? <span className="dashboard-toolbar-error" role="alert">{saveError}</span> : null}
      </div>
      <div className="dashboard-toolbar-right">
        {isCurrentSystem && !canEditVector ? (
          <button
            type="button"
            className="dashboard-toolbar-btn dashboard-toolbar-btn-cta"
            onClick={onCreateOwnDashboard}
            title="Create your own dashboard"
          >
            <IconPlus size={14} />
            <span className="dashboard-cta-label-full">Create your own dashboard</span>
            <span className="dashboard-cta-label-short">Create</span>
          </button>
        ) : isCurrentSystem && canEditVector ? (
          <button
            type="button"
            className={`dashboard-toolbar-btn ${customizing ? 'dashboard-toolbar-btn-active' : 'dashboard-toolbar-btn-cta'}`}
            onClick={onToggleCustomize}
            title={`Edit the ${orgDashboardName()}. Changes are published to all signed-in users.`}
          >
            {customizing ? 'Done' : (
              <>
                <IconSettings size={14} />
                <span className="dashboard-customize-label-full">Customize {orgDashboardName()}</span>
              </>
            )}
          </button>
        ) : canCustomize ? (
          <button
            type="button"
            className={`dashboard-toolbar-btn ${customizing ? 'dashboard-toolbar-btn-active' : ''}`}
            onClick={onToggleCustomize}
            disabled={!current}
          >
            {customizing ? 'Done' : <><IconSettings size={14} /> Customize</>}
          </button>
        ) : null}
      </div>
    </div>
  );
}
